import { css, SerializedStyles } from '@emotion/react';
import React from 'react';
import {
    createResponsiveStyles,
    objectToCssVariables,
    ResponsiveStyleValue,
    SwitchSize,
    useHoneycombTheme,
} from '../../theming';

// Static styles
export const STATIC_STYLES: SerializedStyles = css`
    --switch-height-m: 32px;
    --switch-height-s: 24px;
    --switch-aspect-ratio: 2 / 1;
    --switch-track-padding: 2px;
    --switch-handle-size: calc(var(--switch-height) - var(--switch-track-padding) * 2);
    --switch-icon-size: calc(var(--switch-height) * 0.6);

    position: relative;
    display: inline-block;
    aspect-ratio: var(--switch-aspect-ratio);
    height: var(--switch-height);

    flex-shrink: 0;

    [data-switch-track] {
        padding: var(--switch-track-padding);

        width: 100%;
        height: 100%;

        background-color: var(--switch-color-default);
        border-radius: calc(var(--switch-height) / 2);
        transition: background-color 200ms ease;
    }

    [data-switch-handle] {
        position: absolute;
        inset-inline-start: var(--switch-track-padding);

        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--hc-color-common-white);
        width: var(--switch-handle-size);
        height: var(--switch-handle-size);
        color: var(--hc-color-primary-base);
        border: solid var(--switch-track-padding) transparent;
        border-radius: 50%;
        transition: inset-inline-start 200ms ease;

        svg {
            width: var(--switch-icon-size);
            height: var(--switch-icon-size);
            transform: scale3d(0, 0, 1);
            opacity: 0;
            transition: transform 200ms ease, opacity 200ms ease;
        }
    }

    [data-switch-input] {
        &:checked + div {
            background-color: var(--switch-color-checked);
        }
        &:checked + div > div {
            inset-inline-start: calc(100% - var(--switch-handle-size) - var(--switch-track-padding));
        }
        &:checked + div svg {
            opacity: 1;
            transform: scale3d(1, 1, 1);
        }
        &:disabled + div {
            opacity: var(--hc-disabled-opacity);
            pointer-events: none;
        }

        &:not(:disabled):focus-visible + div > div {
            outline: 2px solid var(--hc-color-action-focus);
        }
    }
`;

export function useDynamicStyles(size: ResponsiveStyleValue<SwitchSize>): React.CSSProperties {
    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.Switch;

    return React.useMemo(() => {
        return {
            ...objectToCssVariables(theme.fn.resolveFlatColorObject(componentTheme.colors), 'switch-color-'),
            ...createResponsiveStyles(
                [
                    {
                        value: size,
                        getStyles: (value: SwitchSize) => {
                            return {
                                '--switch-height': `var(--switch-height-${value})`,
                            };
                        },
                    },
                ],
                theme
            ),
        } as React.CSSProperties;
    }, [componentTheme.colors, size, theme]);
}
